import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  useDisclosure,
  Button,
  Card,
  CardBody,
  RadioGroup,
  Radio,
  Chip,
} from "@heroui/react";
import { IconChevronDown } from "@tabler/icons-react";
import Image from "next/image";
import { SupportedGames } from "@/types/game";
import { getCurrentDomain } from "@/lib/domainConfig";
import { GAMES, getGame, getRoutePrefix } from "@/games";
import { useRouter } from "next/navigation";
import { setLastVisitedGame, getLastVisitedGame } from "@/lib/lastGameStore";
import { useState, useEffect } from "react";

const GAME_LOGOS: Record<SupportedGames, { logo: string; bgColor: string }> = {
  naruto: {
    logo: "https://cdn.tcgfan.com/logos/naruto_white.webp",
    bgColor: "transparent",
  },
  palworld: {
    logo: "https://cdn.tcgfan.com/logos/palworld_white.webp",
    bgColor: "transparent",
  },
  "one-piece": { logo: "/logo/gumgum.png", bgColor: "white" },
  "union-arena": {
    logo: "/logo/TCGfan/Logo-White.png",
    bgColor: "transparent",
  },
  riftbound: { logo: "/logo/TCGfan/Logo-White.png", bgColor: "transparent" },
};

type SelectorEntry = {
  logo: string;
  name: string;
  href: string;
  bgColor: string;
};

function selectorEntry(id: SupportedGames): SelectorEntry {
  const g = getGame(id);
  const gameLogo = GAME_LOGOS[id];
  // Custom names for the selector
  const displayNames: Record<SupportedGames, string> = {
    naruto: "Naruto",
    palworld: "Palworld",
    "one-piece": "One Piece",
    "union-arena": "Union Arena",
    riftbound: "Riftbound",
  };
  return {
    logo: gameLogo.logo,
    bgColor: gameLogo.bgColor,
    name: displayNames[id] || g.name.replace(/ (TCG|Card Game)$/, ""),
    href: getRoutePrefix(id).replace(/\/$/, ""),
  };
}

// Only show these games in the switcher
const ENABLED_GAMES: SupportedGames[] = ["naruto", "palworld"];

export default function GameSelector({
  curGame = "palworld",
}: {
  curGame: SupportedGames;
}) {
  const game = selectorEntry(curGame);

  // Use icon instead of full logo for the button
  const buttonLogo =
    curGame === "palworld"
      ? "https://cdn.tcgfan.com/logos/palworld_icon.webp"
      : game.logo;

  // Get current domain configuration
  const currentHost =
    typeof window !== "undefined" ? window.location.host : "localhost";
  const currentDomain = getCurrentDomain(currentHost);
  const websiteConfig = process.env.NEXT_PUBLIC_WEBSITE_CONFIG;

  // Determine which domain we're actually on
  let actualDomain: "gumgum" | "tcgfan" | "default";
  if (websiteConfig === "tcgfan") {
    actualDomain = "tcgfan";
  } else if (websiteConfig === "gumgum") {
    actualDomain = "gumgum";
  } else if (websiteConfig === "local") {
    actualDomain = "default";
  } else {
    actualDomain = currentDomain;
  }

  const getGameUrl = (gameKey: string) => {
    const gameConfig = selectorEntry(gameKey as SupportedGames);

    // If we're on localhost with local settings, stay on localhost
    if (actualDomain === "default") {
      return gameConfig.href ? `/${gameConfig.href}` : "/";
    }

    // If selecting One Piece on tcgfan domain, go to gumgum.gg
    if (
      getGame(gameKey as SupportedGames).domain.allowedOn === "gumgum" &&
      actualDomain === "tcgfan"
    ) {
      return `https://gumgum.gg/`; // One Piece goes to root on gumgum.gg
    }

    // If selecting Union Arena/Riftbound on gumgum domain, go to tcgfan.com
    if (
      getGame(gameKey as SupportedGames).domain.allowedOn === "tcgfan" &&
      actualDomain === "gumgum"
    ) {
      return `https://tcgfan.com/${gameConfig.href}`;
    }

    // If we're on the same domain as the target game
    if (
      getGame(gameKey as SupportedGames).domain.allowedOn === "gumgum" &&
      actualDomain === "gumgum"
    ) {
      return "/"; // One Piece goes to root on gumgum.gg
    }

    if (
      getGame(gameKey as SupportedGames).domain.allowedOn === "tcgfan" &&
      actualDomain === "tcgfan"
    ) {
      return `/${gameConfig.href}`; // Union Arena/Riftbound go to their paths on tcgfan
    }

    // Default fallback
    return gameConfig.href ? `/${gameConfig.href}` : "/";
  };

  // Filter games to only show enabled ones
  const enabledGames = GAMES.filter((g) => ENABLED_GAMES.includes(g.id));
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const router = useRouter();
  const [defaultGame, setDefaultGame] = useState<SupportedGames | null>(null);

  useEffect(() => {
    // Load the default game from localStorage on mount
    setDefaultGame(getLastVisitedGame());
  }, []);

  const handleGameClick = (gameKey: SupportedGames) => {
    const gameUrl = getGameUrl(gameKey);
    router.push(gameUrl);
  };

  const handleDefaultChange = (value: string) => {
    const gameKey = value as SupportedGames;
    setLastVisitedGame(gameKey);
    setDefaultGame(gameKey);
  };

  return (
    <>
      <Button onPress={onOpen}>
        <div className="flex flex-row items-center gap-1.5">
          <div
            className="rounded-full flex items-center justify-center"
            style={{ backgroundColor: game.bgColor }}
          >
            <Image src={buttonLogo} alt={game.name} width={24} height={24} />
          </div>
          <div>{game.name}</div>
          <div>
            <IconChevronDown size={18} />
          </div>
        </div>
      </Button>

      <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="2xl">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Select a Game
              </ModalHeader>
              <ModalBody className="pb-6">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {enabledGames.map((g) => {
                    const key = g.id;
                    const value = selectorEntry(key);
                    const isSelected = key === curGame;
                    const isDefault = key === defaultGame;
                    return (
                      <div key={key} className="flex flex-col gap-3">
                        <Card
                          isPressable
                          isHoverable
                          onPress={() => {
                            handleGameClick(key);
                            onClose();
                          }}
                          className={`${isSelected ? "border-2 border-primary" : ""} ${isDefault ? "bg-default-100" : ""} relative`}
                        >
                          {isDefault && (
                            <Chip
                              size="sm"
                              color="primary"
                              className="absolute top-2 left-2 z-10"
                            >
                              Default
                            </Chip>
                          )}
                          <CardBody className="p-3">
                            <div className="flex flex-col items-center gap-2">
                              <div
                                className="flex items-center justify-center p-1"
                                style={{
                                  backgroundColor: value.bgColor,
                                  height: "80px",
                                  minWidth: "80px",
                                }}
                              >
                                <Image
                                  src={value.logo}
                                  alt={value.name}
                                  width={160}
                                  height={80}
                                  style={{
                                    objectFit: "contain",
                                    maxHeight: "80px",
                                  }}
                                />
                              </div>
                              <div className="text-sm font-semibold text-center">
                                {value.name}
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                        <RadioGroup
                          value={defaultGame || ""}
                          onValueChange={handleDefaultChange}
                        >
                          <div
                            className="flex items-center justify-center gap-2 cursor-pointer"
                            onClick={() => handleDefaultChange(key)}
                          >
                            <Radio
                              value={key}
                              size="sm"
                              classNames={{
                                base: "m-0 p-0",
                              }}
                            />
                            <span className="text-xs text-gray-400">
                              {isDefault ? "Default" : "Set as default"}
                            </span>
                          </div>
                        </RadioGroup>
                      </div>
                    );
                  })}
                </div>
                <div className="text-xs text-center text-gray-400 pb-2">
                  Your default game will be loaded automatically when you visit
                  TCGfan.com
                </div>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
